import {Box2Like, Size2Like, Vector2Like} from "@cm/math"

// Regions are always integer, in-bounds boxes in full-resolution reference image pixels:
// ImageOpNodeExtractRegion pads with zeros outside the image, which would bias the statistics. The
// minimum size also keeps the region clear of ImageOpNodeMeanAndVariance's bug for 1x1 inputs (see
// compute-lab-mean-from-color.ts).
export const MIN_REFERENCE_REGION_SIZE = 10

export type ReferenceRegionCorner = "nw" | "ne" | "sw" | "se"

export type ReferenceRegionDrag =
    | {type: "create"; start: Vector2Like}
    | {type: "move"; start: Vector2Like; region: Box2Like}
    | {type: "resize"; corner: ReferenceRegionCorner; region: Box2Like}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const minSizeFor = (size: number) => Math.min(MIN_REFERENCE_REGION_SIZE, size)

// the span between two points along one axis, grown in the drag direction if it is below the minimum size
const spanBetween = (start: number, end: number, size: number): [number, number] => {
    const clampedStart = clamp(Math.round(start), 0, size)
    const clampedEnd = clamp(Math.round(end), 0, size)
    const minSize = minSizeFor(size)
    if (Math.abs(clampedEnd - clampedStart) >= minSize) {
        return [Math.min(clampedStart, clampedEnd), Math.max(clampedStart, clampedEnd)]
    }
    return clampedEnd >= clampedStart
        ? [Math.min(clampedStart, size - minSize), Math.min(clampedStart, size - minSize) + minSize]
        : [Math.max(clampedStart - minSize, 0), Math.max(clampedStart - minSize, 0) + minSize]
}

export const applyReferenceRegionDrag = (drag: ReferenceRegionDrag, point: Vector2Like, imageSize: Size2Like): Box2Like => {
    switch (drag.type) {
        case "create": {
            const [x1, x2] = spanBetween(drag.start.x, point.x, imageSize.width)
            const [y1, y2] = spanBetween(drag.start.y, point.y, imageSize.height)
            return {x: x1, y: y1, width: x2 - x1, height: y2 - y1}
        }
        case "move": {
            const {region, start} = drag
            return {
                x: clamp(Math.round(region.x + point.x - start.x), 0, imageSize.width - region.width),
                y: clamp(Math.round(region.y + point.y - start.y), 0, imageSize.height - region.height),
                width: region.width,
                height: region.height,
            }
        }
        case "resize": {
            const {region, corner} = drag
            const minWidth = minSizeFor(imageSize.width)
            const minHeight = minSizeFor(imageSize.height)
            const right = region.x + region.width
            const bottom = region.y + region.height
            const x1 = corner === "nw" || corner === "sw" ? clamp(Math.round(point.x), 0, right - minWidth) : region.x
            const x2 = corner === "ne" || corner === "se" ? clamp(Math.round(point.x), region.x + minWidth, imageSize.width) : right
            const y1 = corner === "nw" || corner === "ne" ? clamp(Math.round(point.y), 0, bottom - minHeight) : region.y
            const y2 = corner === "sw" || corner === "se" ? clamp(Math.round(point.y), region.y + minHeight, imageSize.height) : bottom
            return {x: x1, y: y1, width: x2 - x1, height: y2 - y1}
        }
    }
}

// A persisted region may no longer fit, e.g. when the referenced map has been re-baked at another
// resolution. Returns undefined if nothing usable of it is left.
export const normalizeReferenceRegion = (region: Box2Like, imageSize: Size2Like): Box2Like | undefined => {
    const x1 = clamp(Math.round(region.x), 0, imageSize.width)
    const y1 = clamp(Math.round(region.y), 0, imageSize.height)
    const x2 = clamp(Math.round(region.x + region.width), 0, imageSize.width)
    const y2 = clamp(Math.round(region.y + region.height), 0, imageSize.height)
    if (x2 - x1 < minSizeFor(imageSize.width) || y2 - y1 < minSizeFor(imageSize.height)) {
        return undefined
    }
    return {x: x1, y: y1, width: x2 - x1, height: y2 - y1}
}
